// Sports Arena — shared constants + React state hook.
//
// The arena lives in the open south-east corner of the Campus Quad. It hosts
// one active sport at a time (Basketball or Soccer); the player faces off
// against four AI opponents whose speed + accuracy scale with the selected
// difficulty. Scoring is session-scoped: refresh the page and it resets.

import { useCallback, useMemo, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Geometry constants — world coordinates
// ---------------------------------------------------------------------------

// Arena entrance / plaza on the Campus Quad. The "Go to Arena" button in
// the HUD teleports the visitor here. All three sports fan out from this
// central plaza — basketball to the north, football to the south, soccer
// to the east — so that the entire sports complex is visible at once.
export const ARENA_CENTER = [40, 0, 40];
export const ENTRANCE_CENTER = ARENA_CENTER; // alias for readability

// Per-sport centres. Chosen so the four footprints don't overlap and each
// sport gets a clear approach corridor from the entrance plaza. Baseball
// was pushed further west after its 2× scale-up so its outfield doesn't
// clip the west-wing buildings.
//   basketball → north (−Z)     18×11 court
//   football   → south (+Z)     30×16 gridiron
//   soccer     → east  (+X)     22×14 pitch
//   baseball   → west  (−X)     enlarged diamond + arced outfield
export const SPORT_CENTERS = {
  basketball: [45, 0, 20],
  football:   [40, 0, 70],
  soccer:     [70, 0, 40],
  baseball:   [-45, 0, 55],
};

/** Look up the world-space centre of a given sport's field/court. */
export function getSportCenter(sport) {
  return SPORT_CENTERS[sport] || ARENA_CENTER;
}

// Court dimensions are chosen so both sports share an "obvious footprint"
// visible from anywhere on the quad. Long axis runs along +X, so the two
// goals face each other east-west.
export const BASKETBALL_COURT = {
  length: 18, // along X
  width: 11,  // along Z
  hoopHeight: 3.05,
  hoopReach: 6.5, // distance from centre to each hoop along X
  colors: {
    floor: "#e6844a",   // Roblox-orange hardwood
    floorAlt: "#f2a878",
    line: "#ffffff",
    key: "#7a8bff",     // painted key
  },
};

export const SOCCER_PITCH = {
  length: 22, // along X
  width: 14,  // along Z
  goalWidth: 3.2,
  goalHeight: 2.0,
  goalDepth: 1.0,
  goalReach: 9.5, // distance from centre to each goal-line along X
  colors: {
    turf: "#4bb352",     // saturated cartoon green
    turfAlt: "#5cc65e",
    line: "#ffffff",
  },
};

export const FOOTBALL_FIELD = {
  length: 30,     // along X — includes 4m end zones on each side
  width: 16,      // along Z
  endZoneDepth: 4,
  colors: {
    turf: "#3a8f43",       // deep gridiron green
    turfAlt: "#4aa056",
    endZone: "#e6b450",    // gold end zones for the visitor's team
    endZoneAway: "#7d4dd6",// purple end zones for the CPU
    line: "#ffffff",
  },
};

// Baseball diamond (Home Run Derby mode). Home plate sits on the east edge
// of the diamond; the batter stands there facing west so the ball flies
// -X after contact. The pitcher NPC stands on the mound between the plate
// and 2nd base. The home-run wall sits at homeRunDistance metres west of
// home plate; a hit that crosses the wall before touching the ground is a
// dinger.
export const BASEBALL_DIAMOND = {
  size: 26,             // side of the diamond square (base-to-base)
  plateOffset: 12,      // home plate is +plateOffset east of the sport centre
  pitcherOffset: 5,     // (unused after mound relocation to diamond centre)
  homeRunDistance: 26,  // distance from home plate (in +X units) for a HR
  outfieldRadius: 30,   // radius of the arced outfield surface from home plate
  pitchSpeed: 9,        // metres/second baseline (scaled by difficulty)
  colors: {
    dirt: "#c58a4f",
    grass: "#3f9c4d",
    grassAlt: "#4bb352",
    base: "#f7f5ee",
    homePlate: "#f7f5ee",
    mound: "#a56b3a",
    line: "#ffffff",
    fence: "#0b1530",
    fenceTop: "#e6b450",
  },
};

// Distance from player camera to ball inside which a click counts as a kick.
export const PLAYER_KICK_RADIUS = 3.2;

// Distance from an opponent NPC to the ball inside which the NPC can shoot.
export const NPC_KICK_RADIUS = 1.4;

// The "player goal" (the one PLAYER defends → CPU tries to score here) sits
// on the -X side of the arena. The "cpu goal" (player tries to score here)
// sits on the +X side.
export const GOAL_SIDE = {
  playerDefends: -1, // -X side
  playerAttacks: +1, // +X side
};

// ---------------------------------------------------------------------------
// Sport configs — physics + goal AABBs (world coordinates)
// ---------------------------------------------------------------------------

const [bx, , bz] = SPORT_CENTERS.basketball;
const [sx, , sz] = SPORT_CENTERS.soccer;
const [fx, , fz] = SPORT_CENTERS.football;

/** Axis-aligned bounding box for a goal, used for scoring detection. */
function makeGoalBox({ cx, cy, cz, sx, sy, sz }) {
  return {
    min: [cx - sx / 2, cy - sy / 2, cz - sz / 2],
    max: [cx + sx / 2, cy + sy / 2, cz + sz / 2],
    center: [cx, cy, cz],
  };
}

export const SPORTS = {
  basketball: {
    id: "basketball",
    label: "Basketball",
    ballRadius: 0.28,
    ballColor: "#e67c2b",
    ballAccent: "#3a2015",
    ballRestitution: 0.72,
    ballLinearDamping: 0.35,
    // A "goal" in our simplified scoring model is a small AABB directly under
    // each hoop rim. If the ball's centre passes through the box moving
    // downward, we count it as a bucket.
    // The goal AABB is aligned to the actual rim position (rim sits 0.15m
    // inward from the hoopReach anchor — see BasketballHoop below) and
    // sized so a ball descending through the hoop reliably lands inside.
    // The vertical band (cy ± 0.35) covers the natural drop from rim to
    // net floor at ball radius 0.28m.
    playerGoal: makeGoalBox({
      cx: bx - (BASKETBALL_COURT.hoopReach - 0.15),
      cy: BASKETBALL_COURT.hoopHeight - 0.1,
      cz: bz,
      sx: 1.1,
      sy: 0.7,
      sz: 1.1,
    }),
    cpuGoal: makeGoalBox({
      cx: bx + (BASKETBALL_COURT.hoopReach - 0.15),
      cy: BASKETBALL_COURT.hoopHeight - 0.1,
      cz: bz,
      sx: 1.1,
      sy: 0.7,
      sz: 1.1,
    }),
    // Impulse magnitudes are ball-mass-normalised; the useArenaPhysics hook
    // scales them by the ball's mass automatically.
    playerKickImpulse: 5.5,
    npcKickImpulseBase: 4.8,
  },
  soccer: {
    id: "soccer",
    label: "Soccer",
    ballRadius: 0.22,
    ballColor: "#ffffff",
    ballAccent: "#111827",
    ballRestitution: 0.55,
    ballLinearDamping: 0.55,
    playerGoal: makeGoalBox({
      cx: sx - SOCCER_PITCH.goalReach + SOCCER_PITCH.goalDepth / 2,
      cy: SOCCER_PITCH.goalHeight / 2,
      cz: sz,
      sx: SOCCER_PITCH.goalDepth,
      sy: SOCCER_PITCH.goalHeight,
      sz: SOCCER_PITCH.goalWidth,
    }),
    cpuGoal: makeGoalBox({
      cx: sx + SOCCER_PITCH.goalReach - SOCCER_PITCH.goalDepth / 2,
      cy: SOCCER_PITCH.goalHeight / 2,
      cz: sz,
      sx: SOCCER_PITCH.goalDepth,
      sy: SOCCER_PITCH.goalHeight,
      sz: SOCCER_PITCH.goalWidth,
    }),
    playerKickImpulse: 6.5,
    npcKickImpulseBase: 5.6,
  },
  football: {
    id: "football",
    label: "Football",
    // The football uses a scaled sphere collider (a Rapier ball) — we
    // cheat visually with an ellipsoid mesh so it *looks* like a real
    // football while still colliding predictably.
    ballRadius: 0.16,
    ballColor: "#8b4a2b",
    ballAccent: "#f3ead4",
    ballRestitution: 0.35, // low bounce vs the round balls
    ballLinearDamping: 0.75,
    // The "goal" for football is the CPU end zone — a large AABB on the +X
    // side of the field. Any teammate catch + player QB pass whose ball
    // touches the end zone counts as a TD in the scoring model.
    playerGoal: makeGoalBox({
      cx: fx - FOOTBALL_FIELD.length / 2 + FOOTBALL_FIELD.endZoneDepth / 2,
      cy: 0.5,
      cz: fz,
      sx: FOOTBALL_FIELD.endZoneDepth,
      sy: 2.0,
      sz: FOOTBALL_FIELD.width,
    }),
    cpuGoal: makeGoalBox({
      cx: fx + FOOTBALL_FIELD.length / 2 - FOOTBALL_FIELD.endZoneDepth / 2,
      cy: 0.5,
      cz: fz,
      sx: FOOTBALL_FIELD.endZoneDepth,
      sy: 2.0,
      sz: FOOTBALL_FIELD.width,
    }),
    // The player never "kicks" in football — click-to-snap is instant,
    // and click-to-throw applies an aim-toward-target impulse. Values
    // below are used as the throw impulse magnitude.
    playerKickImpulse: 7.5,
    npcKickImpulseBase: 6.0,
    // 7v7 setup: player is QB + 6 teammates vs 7 defenders.
    teammateCount: 6,
    defenderCount: 7,
  },
  baseball: {
    id: "baseball",
    label: "Baseball",
    scoreLabel: "HR",       // Home Runs instead of Goals
    ballColor: "#f7f5ee",
    ballAccent: "#c8353a",
    ballRadius: 0.14,       // MLB baseball ≈ 74mm diameter → 0.037m; we scale up 4x for readability
    playerKickImpulse: 12.0,
    npcKickImpulseBase: 0,  // no NPC swings — pitcher only tosses
    // A dummy AABB well below the arena so `insideBox` never fires for it —
    // baseball scoring is distance-based (home run wall) inside SportsArena
    // rather than goal-collision-based like the other three sports.
    playerGoal: { min: [-9999, -9999, -9999], max: [-9998, -9998, -9998], center: [-9999, -9999, -9999] },
    cpuGoal:    { min: [-9999, -9999, -9999], max: [-9998, -9998, -9998], center: [-9999, -9999, -9999] },
    teammateCount: 0,
    defenderCount: 0,
  },
};

// ---------------------------------------------------------------------------
// Difficulty presets
// ---------------------------------------------------------------------------
//
// - `speed`         : NPC movement speed toward the ball (m/s)
// - `aimJitter`     : Random angular offset applied to each NPC shot (radians).
//                     Combined with `successRate` this gives the requested
//                     success-percentage per shot: on a successful roll, we
//                     apply zero jitter; on a failed roll, we apply the full
//                     jitter magnitude so the shot sprays wide.
// - `successRate`   : Probability a given shot is "clean" (0..1).
// - `cooldownMs`    : Time between shot attempts once in range.
// - `reactionDelayMs` : Delay before an NPC starts chasing a newly-reset ball.
//
// The four difficulty tiers map directly to the user's spec:
//   Easy       : slow movement, 30% success rate
//   Basic      : standard movement, 50% success rate
//   Difficult  : faster movement, 75% success rate
//   Hall of Fame: aggressive movement, perfect aim (100%)
export const DIFFICULTIES = {
  easy: {
    id: "easy",
    label: "Easy",
    tagline: "Loose defenders, wide shots",
    speed: 1.8,
    aimJitter: 0.55,
    successRate: 0.30,
    cooldownMs: 2200,
    reactionDelayMs: 700,
  },
  basic: {
    id: "basic",
    label: "Basic",
    tagline: "Standard opponents",
    speed: 2.8,
    aimJitter: 0.4,
    successRate: 0.50,
    cooldownMs: 1600,
    reactionDelayMs: 400,
  },
  difficult: {
    id: "difficult",
    label: "Difficult",
    tagline: "Fast press, sharp aim",
    speed: 3.9,
    aimJitter: 0.22,
    successRate: 0.75,
    cooldownMs: 1100,
    reactionDelayMs: 200,
  },
  hof: {
    id: "hof",
    label: "Hall of Fame",
    tagline: "Perfect aim, aggressive close",
    speed: 5.4,
    aimJitter: 0.02,
    successRate: 1.0,
    cooldownMs: 700,
    reactionDelayMs: 80,
  },
};

export const DIFFICULTY_ORDER = ["easy", "basic", "difficult", "hof"];
export const SPORT_ORDER = ["basketball", "soccer", "football", "baseball"];

// ---------------------------------------------------------------------------
// Team colours — Roblox-style saturated primaries
// ---------------------------------------------------------------------------
export const TEAM_COLORS = {
  player: "#3b82f6",    // player = blue jersey (for future rendered avatar)
  cpu: ["#ef4444", "#f97316", "#eab308", "#a855f7"], // 4 CPU jerseys
};

// The player can pick their team colour from this palette. It re-tints
// the player-side goal net + gets exposed to any avatar renderer that
// wants to reflect team identity on the walker.
export const PLAYER_TEAM_OPTIONS = [
  { id: "royal", label: "Royal", color: "#3b82f6" },
  { id: "flame", label: "Flame", color: "#ef4444" },
  { id: "forest", label: "Forest", color: "#22c55e" },
  { id: "amber", label: "Amber", color: "#f59e0b" },
];
export const DEFAULT_PLAYER_TEAM_ID = "royal";
export function getTeamColorById(id) {
  const found = PLAYER_TEAM_OPTIONS.find((t) => t.id === id);
  return (found || PLAYER_TEAM_OPTIONS[0]).color;
}

// ---------------------------------------------------------------------------
// React state hook — one source of truth for HUD + physics
// ---------------------------------------------------------------------------

const STORAGE_KEY_SPORT = "tab-arena-sport";
const STORAGE_KEY_DIFFICULTY = "tab-arena-difficulty";
const STORAGE_KEY_TEAM = "tab-arena-team";
const STORAGE_KEY_MATCH_INVITE = "tab-arena-match-invite";
const STORAGE_KEY_ROLE = "tab-arena-role";

export const FOOTBALL_ROLES = [
  { id: "qb", label: "QB",  tagline: "Quarterback — snap + throw the ball yourself." },
  { id: "wr", label: "WR",  tagline: "Wide Receiver — CPU QB throws to you; catch and score." },
  { id: "rb", label: "RB",  tagline: "Running Back — receive short passes and rush upfield." },
  { id: "db", label: "DB",  tagline: "Defensive Back — chase the ball and try to intercept." },
];
export const FOOTBALL_ROLE_ORDER = ["qb", "wr", "rb", "db"];

function readStored(key, allow, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const v = window.localStorage.getItem(key);
    if (v && allow.includes(v)) return v;
  } catch (e) {
    /* ignore */
  }
  return fallback;
}

/**
 * useArenaState — the single source of truth for the sports arena.
 *
 * Returns:
 *   {
 *     sport, setSport            : "basketball" | "soccer"
 *     difficulty, setDifficulty  : "easy" | "basic" | "difficult" | "hof"
 *     scores                     : { player, cpu }
 *     addScore(who)              : bump player | cpu, returns new totals
 *     resetScores()              : zero the scoreboard
 *     lastGoal, flashGoal(who)   : brief flash flag used to show "GOAL!"
 *     arenaOpen, setArenaOpen    : boolean — is the HUD panel expanded?
 *     scoresRef                  : live ref (avoids re-reading state in useFrame)
 *   }
 */
export function useArenaState() {
  const [sport, setSportState] = useState(() =>
    readStored(STORAGE_KEY_SPORT, SPORT_ORDER, "basketball")
  );
  const [difficulty, setDifficultyState] = useState(() =>
    readStored(STORAGE_KEY_DIFFICULTY, DIFFICULTY_ORDER, "basic")
  );
  const [teamId, setTeamIdState] = useState(() =>
    readStored(
      STORAGE_KEY_TEAM,
      PLAYER_TEAM_OPTIONS.map((t) => t.id),
      DEFAULT_PLAYER_TEAM_ID
    )
  );
  // Football role: which position you're playing. Only meaningful when
  // sport === "football". Persisted so a returning visitor stays in role.
  const [role, setRoleState] = useState(() =>
    readStored(STORAGE_KEY_ROLE, FOOTBALL_ROLE_ORDER, "qb")
  );
  // Match Invite: when ON, remote peers' kicks apply to your ball, and your
  // kicks are broadcast so their balls move too — turning co-presence into
  // head-to-head play. Persisted so a returning visitor stays in match mode.
  const [matchInvite, setMatchInviteState] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY_MATCH_INVITE) === "1";
    } catch (e) {
      return false;
    }
  });

  const [scores, setScores] = useState({ player: 0, cpu: 0 });
  const scoresRef = useRef(scores);
  scoresRef.current = scores;

  // Baseball / Home Run Derby end-of-session flag. SportsArena flips this
  // true when 10 pitches have been thrown; GameOverOverlay reads it to
  // decide whether to show the "Derby Complete" card.
  const [derbyDone, setDerbyDone] = useState(false);

  const [lastGoal, setLastGoal] = useState(null); // { who, at } | null
  const [arenaOpen, setArenaOpen] = useState(false);
  // Live count of remote visitors on the same court, written by the arena
  // scene when useArenaPresence's WebSocket roster updates.
  const [peerCount, setPeerCount] = useState(0);
  // Live count of remote peers with Match Invite ON — used by the HUD to
  // show a "N READY" matchmaking badge.
  const [readyCount, setReadyCount] = useState(0);
  // Whether the visitor is currently viewing the arena through the
  // Spectator Cam. Drives the DOM broadcast-overlay skin.
  const [broadcastActive, setBroadcastActive] = useState(false);
  // Ref exposed so SportsArena can register a `fire()` function that
  // ArenaTouchControls calls when the on-screen FIRE button is tapped.
  // SportsArena writes { fire: () => void, setSwingDir: (dir:-1|0|1)=>void }
  // into `.current`.
  const touchFireRef = useRef(null);
  // Ref exposed so SportsArena tracks the last chosen swing direction for
  // baseball's bat-path aim system. -1 = opposite (right-field), 0 =
  // straightaway (centre), +1 = pull (left-field). Written by both the
  // touch bat-path buttons and the desktop shortcut keys (Q/A/E → pull /
  // straight / opposite).
  const swingDirRef = useRef(0);

  // Ref for external components (ZoneScene / Walker) to register a
  // teleport callback. `.current = ({ pos, sport }) => void`. Called by
  // the HUD's "Go to Arena" button to snap the walker + camera to the
  // entrance plaza or a specific sport's centre.
  const teleportRef = useRef(null);
  // Live camera-position mirror written by SportsArena every frame; read
  // by the DOM LiveMinimap via a 250ms poll to render the "You Are Here"
  // dot without hooking into the react-three-fiber render loop.
  const playerPosRef = useRef({ x: 0, y: 0, z: 0 });

  const setSport = useCallback((s) => {
    if (!SPORT_ORDER.includes(s)) return;
    setSportState(s);
    setScores({ player: 0, cpu: 0 });
    setLastGoal(null);
    try {
      window.localStorage.setItem(STORAGE_KEY_SPORT, s);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const setDifficulty = useCallback((d) => {
    if (!DIFFICULTY_ORDER.includes(d)) return;
    setDifficultyState(d);
    try {
      window.localStorage.setItem(STORAGE_KEY_DIFFICULTY, d);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const setTeamId = useCallback((id) => {
    if (!PLAYER_TEAM_OPTIONS.some((t) => t.id === id)) return;
    setTeamIdState(id);
    try {
      window.localStorage.setItem(STORAGE_KEY_TEAM, id);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const setMatchInvite = useCallback((on) => {
    const v = !!on;
    setMatchInviteState(v);
    try {
      window.localStorage.setItem(STORAGE_KEY_MATCH_INVITE, v ? "1" : "0");
    } catch (e) {
      /* ignore */
    }
  }, []);

  const setRole = useCallback((r) => {
    if (!FOOTBALL_ROLE_ORDER.includes(r)) return;
    setRoleState(r);
    try {
      window.localStorage.setItem(STORAGE_KEY_ROLE, r);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const addScore = useCallback((who) => {
    setScores((s) => {
      const next = { ...s, [who]: s[who] + 1 };
      scoresRef.current = next;
      return next;
    });
    setLastGoal({ who, at: Date.now() });
  }, []);

  const resetScores = useCallback(() => {
    const zero = { player: 0, cpu: 0 };
    setScores(zero);
    scoresRef.current = zero;
    setLastGoal(null);
  }, []);

  const value = useMemo(
    () => ({
      sport,
      setSport,
      difficulty,
      setDifficulty,
      teamId,
      setTeamId,
      teamColor: getTeamColorById(teamId),
      matchInvite,
      setMatchInvite,
      role,
      setRole,
      scores,
      scoresRef,
      addScore,
      resetScores,
      lastGoal,
      arenaOpen,
      setArenaOpen,
      peerCount,
      _setPeerCount: setPeerCount,
      readyCount,
      _setReadyCount: setReadyCount,
      broadcastActive,
      setBroadcastActive,
      touchFireRef,
      swingDirRef,
      teleportRef,
      playerPosRef,
      derbyDone,
      setDerbyDone,
    }),
    [
      sport,
      setSport,
      difficulty,
      setDifficulty,
      teamId,
      setTeamId,
      scores,
      addScore,
      resetScores,
      lastGoal,
      arenaOpen,
      peerCount,
      readyCount,
      matchInvite,
      setMatchInvite,
      role,
      setRole,
      broadcastActive,
    ]
  );

  return value;
}
